import { memo, useMemo } from 'react';
import { Divider, Stack } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import {
  selectDimensionItemIds,
  selectFlowsByIds,
  selectGroupsByIds,
  selectHikStoragesByIds,
  selectLassieMinisByIds,
  selectLassiesByIds,
  selectLogicalSpacesByIds,
  selectObstaclesByIds,
  selectSelectedHighwayAreas,
  selectSelectedManualAreas,
  selectSelectedRackingAreas,
  selectSelectedRegularObstacles,
  selectSelectedSingularStationAreas,
  selectSelectedStationaryAreas,
  selectSelectedWalls,
  selectShuttleRacksByIds,
  selectTemplatesByIds,
  selectTopLevelSelectedIds,
} from '@/modules/floorplanDesign/selectors/selectedEntitiesSelectors';
import { HighwayAreaParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/HighwayAreaParameters';
import { SingularStationAreaParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/SingularStationAreaParameters';
import { StationaryAreaParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/StationaryAreaParameters';
import { RackingAreaParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/RackingAreaParameters';
import { LayerProperties } from '@/modules/layers/components/LayerProperties';
import { FlowsPanel } from '@/modules/flowsV2/components/layout/';
import { GroupsProperties } from '@/modules/groupsV2/components/GroupsProperties';
import { Header } from '@/modules/salesWorkspace/components/propertyPanel/Header';
import { getSelectionHeaderState } from '@/modules/salesWorkspace/components/propertyPanel/Header/helpers';
import {
  LassieTemplateParameters,
  LassieMiniTemplateParameters,
  ShuttleTemplateParameters,
  HikTemplateParameters,
} from '@/modules/templating/components/';
import { ObstacleParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/ObstacleParameters';
import { WallParameters } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/WallParameters';
import { FlowStopPanel } from '@/modules/flowStop';

import { ManualAreaParameters } from './components/manual/ManualAreaParameters';
import { LogicalSpaceParameters } from './components/logicalSpace';
import { SelectionDimensions } from './components/dimensions/SelectionDimensions';
import { getSelectionKind } from './components/dimensions/helpers';
import { PANELS_SPACING } from '@/modules/common/constants/propertyPanel';

function LayoutComponent() {
  const selectedIds = useAppStore((state) => state.selectedIds);
  // Without what's inside selected templates and groups: the header shows those as one item
  const itemIds = useFloorplanDesignStore(
    useShallow((state) => selectTopLevelSelectedIds(state, selectedIds)),
  );
  // Selected groups replaced by their items: those get the normal panels and dimensions
  const editableItemIds = useFloorplanDesignStore(
    useShallow((state) => selectDimensionItemIds(state, selectedIds)),
  );
  const selectedIdSet = useMemo(() => new Set(editableItemIds), [editableItemIds]);

  // Each panel gets its own shallow-compared list, so editing one type of item doesn't
  // re-render the panels of the other types
  const selectedHighwayAreas = useFloorplanDesignStore(
    useShallow((state) => selectSelectedHighwayAreas(state, selectedIdSet)),
  );
  const selectedRackingAreas = useFloorplanDesignStore(
    useShallow((state) => selectSelectedRackingAreas(state, selectedIdSet)),
  );
  const selectedSingularStationAreas = useFloorplanDesignStore(
    useShallow((state) => selectSelectedSingularStationAreas(state, selectedIdSet)),
  );
  const selectedStationaryAreas = useFloorplanDesignStore(
    useShallow((state) => selectSelectedStationaryAreas(state, selectedIdSet)),
  );
  const selectedManualAreas = useFloorplanDesignStore(
    useShallow((state) => selectSelectedManualAreas(state, selectedIdSet)),
  );
  const selectedWalls = useFloorplanDesignStore(
    useShallow((state) => selectSelectedWalls(state, selectedIdSet)),
  );
  const selectedRegularObstacles = useFloorplanDesignStore(
    useShallow((state) => selectSelectedRegularObstacles(state, selectedIdSet)),
  );

  // Same order as areasToFlatCollection
  const selectedAreas = useMemo(
    () => [
      ...selectedHighwayAreas,
      ...selectedRackingAreas,
      ...selectedSingularStationAreas,
      ...selectedStationaryAreas,
      ...selectedManualAreas,
    ],
    [
      selectedHighwayAreas,
      selectedRackingAreas,
      selectedSingularStationAreas,
      selectedStationaryAreas,
      selectedManualAreas,
    ],
  );
  const selectedObstacles = useFloorplanDesignStore(
    useShallow((state) => selectObstaclesByIds(state, editableItemIds)),
  );
  const selectedTemplates = useFloorplanDesignStore(
    useShallow((state) => selectTemplatesByIds(state, itemIds)),
  );
  const selectedLogicalSpaces = useFloorplanDesignStore(
    useShallow((state) => selectLogicalSpacesByIds(state, editableItemIds)),
  );
  const selectedGroups = useFloorplanDesignStore(
    useShallow((state) => selectGroupsByIds(state, itemIds)),
  );
  const selectedFlows = useFloorplanDesignStore(
    useShallow((state) => selectFlowsByIds(state, selectedIds)),
  );

  const selectedLassies = useFloorplanDesignStore(
    useShallow((state) => selectLassiesByIds(state, itemIds)),
  );
  const selectedLassieMinis = useFloorplanDesignStore(
    useShallow((state) => selectLassieMinisByIds(state, itemIds)),
  );
  const selectedShuttleRacks = useFloorplanDesignStore(
    useShallow((state) => selectShuttleRacksByIds(state, itemIds)),
  );
  const selectedHikStorages = useFloorplanDesignStore(
    useShallow((state) => selectHikStoragesByIds(state, itemIds)),
  );

  const hasSelectedGroups = selectedGroups.length > 0;

  // Flows connect to the areas inside templates and groups, not to the template or group itself
  const relatedItemIds = useMemo(
    () => [...editableItemIds, ...selectedTemplates.flatMap(({ group: { areaIds } }) => areaIds)],
    [editableItemIds, selectedTemplates],
  );

  // The header counts a selected group as one item, not its items
  const selectionHeader = useMemo(() => {
    const itemIdSet = new Set(itemIds);
    const isTopLevel = ({ id }: { id: string }) => !hasSelectedGroups || itemIdSet.has(id);

    return getSelectionHeaderState({
      selectedAreas: selectedAreas.filter(isTopLevel),
      selectedObstacles: selectedObstacles.filter(isTopLevel),
      selectedTemplates,
      selectedLogicalSpaces: selectedLogicalSpaces.filter(isTopLevel),
      selectedGroups,
      selectedFlows,
    });
  }, [
    hasSelectedGroups,
    itemIds,
    selectedAreas,
    selectedObstacles,
    selectedTemplates,
    selectedLogicalSpaces,
    selectedGroups,
    selectedFlows,
  ]);

  // Decided here instead of in SelectionDimensions: the Stack draws a divider around every child,
  // also around one that renders nothing (e.g. when a template is selected)
  const dimensionKind = useMemo(
    () =>
      getSelectionKind(
        editableItemIds,
        selectedAreas,
        selectedObstacles,
        selectedLogicalSpaces,
        selectedTemplates,
      ),
    [editableItemIds, selectedAreas, selectedObstacles, selectedLogicalSpaces, selectedTemplates],
  );

  return (
    <Stack divider={<Divider />} spacing={PANELS_SPACING}>
      <Header
        selectionHeader={selectionHeader}
        selectedSingularStationAreas={selectedSingularStationAreas}
        selectedRackingAreas={selectedRackingAreas}
        // selectedItems={selectedItems}
      />
      {dimensionKind && (
        <SelectionDimensions
          kind={dimensionKind}
          selectedIds={selectedIds}
          selectedAreas={selectedAreas}
          selectedObstacles={selectedObstacles}
          selectedLogicalSpaces={selectedLogicalSpaces}
          selectedTemplates={selectedTemplates}
          hasSelectedGroups={hasSelectedGroups}
        />
      )}

      {selectedHighwayAreas.length > 0 && (
        <HighwayAreaParameters selectedHighwayAreas={selectedHighwayAreas} />
      )}
      {selectedSingularStationAreas.length > 0 && (
        <SingularStationAreaParameters
          selectedSingularStationAreas={selectedSingularStationAreas}
        />
      )}

      {selectedStationaryAreas.length > 0 && (
        <StationaryAreaParameters selectedStationaryAreas={selectedStationaryAreas} />
      )}
      {selectedRackingAreas.length > 0 && (
        <RackingAreaParameters selectedRackingAreas={selectedRackingAreas} />
      )}
      {selectedManualAreas.length > 0 && (
        <ManualAreaParameters selectedManualAreas={selectedManualAreas} />
      )}
      {selectedLassies.length > 0 && <LassieTemplateParameters selectedLassies={selectedLassies} />}
      {selectedLassieMinis.length > 0 && (
        <LassieMiniTemplateParameters selectedLassieMinis={selectedLassieMinis} />
      )}
      {selectedShuttleRacks.length > 0 && (
        <ShuttleTemplateParameters selectedShuttleRacks={selectedShuttleRacks} />
      )}
      {selectedHikStorages && selectedHikStorages.length > 0 && (
        <HikTemplateParameters selectedHikStorages={selectedHikStorages} />
      )}
      {selectedLogicalSpaces.length > 0 && (
        <LogicalSpaceParameters selectedLogicalSpaces={selectedLogicalSpaces} />
      )}
      {selectedWalls.length > 0 && <WallParameters selectedWalls={selectedWalls} />}
      {selectedRegularObstacles.length > 0 && (
        <ObstacleParameters selectedObstacles={selectedRegularObstacles} />
      )}
      <FlowsPanel relatedItemIds={relatedItemIds} selectedFlows={selectedFlows} />
      {/* Selected groups replaced by their items: a flow stop holds areas, not groups */}
      <FlowStopPanel selectedIds={editableItemIds} />
      <GroupsProperties />
      <LayerProperties />
    </Stack>
  );
}

export const Layout = memo(LayoutComponent);
